import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CoverImage } from '@/components/CoverImage';
import { Star, Trophy, Medal, Award } from 'lucide-react';

interface RankedBook {
  id: string;
  title: string;
  authors?: string[];
  cover_url?: string;
  cover_cache_url?: string;
  isbn13?: string;
  isbn10?: string;
  rating_star: number;
  finished_at?: string;
  preference_score?: number;
}

interface PersonalRankingsProps {
  books: RankedBook[];
  year: number;
  maxBooks?: number;
}

const rankIcons = [
  { icon: Trophy, color: 'text-yellow-500' },
  { icon: Medal, color: 'text-slate-400' },
  { icon: Award, color: 'text-amber-600' },
];

export function PersonalRankings({ books, year, maxBooks = 10 }: PersonalRankingsProps) {
  const navigate = useNavigate();
  
  // Sort by rating (highest first), then by preference_score (highest first), then by finish date
  const rankedBooks = useMemo(() => {
    return [...books]
      .filter(b => b.rating_star && b.rating_star >= 4) // Only show highly rated books
      .sort((a, b) => {
        if (b.rating_star !== a.rating_star) {
          return b.rating_star - a.rating_star;
        }
        // Secondary sort by preference_score (pairwise ranking)
        const scoreA = a.preference_score ?? 0;
        const scoreB = b.preference_score ?? 0;
        if (scoreB !== scoreA) {
          return scoreB - scoreA;
        }
        // Tertiary sort by finish date (most recent first)
        const dateA = a.finished_at ? new Date(a.finished_at).getTime() : 0;
        const dateB = b.finished_at ? new Date(b.finished_at).getTime() : 0;
        return dateB - dateA;
      })
      .slice(0, maxBooks);
  }, [books, maxBooks]);
  
  if (rankedBooks.length === 0) return null;

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          Your Top Books of {year}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Your highest rated reads this year
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {rankedBooks.map((book, index) => {
            const RankIcon = rankIcons[index]?.icon || null;
            const rankColor = rankIcons[index]?.color || 'text-muted-foreground';
            
            return (
              <button
                key={book.id}
                onClick={() => navigate(`/book/${book.id}`)}
                className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-accent/50 transition-colors text-left group"
              >
                {/* Rank number */}
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                  {RankIcon ? (
                    <RankIcon className={`w-6 h-6 ${rankColor}`} />
                  ) : (
                    <span className="text-lg font-bold text-muted-foreground">
                      {index + 1}
                    </span>
                  )}
                </div>
                
                {/* Book cover */}
                <CoverImage
                  bookId={book.id}
                  isbn13={book.isbn13}
                  isbn10={book.isbn10}
                  title={book.title}
                  author={book.authors?.[0]}
                  size="xs"
                  className="w-10 h-14 rounded shadow-sm flex-shrink-0 group-hover:shadow-md transition-shadow"
                />
                
                {/* Book info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                    {book.title}
                  </h4>
                  <p className="text-xs text-muted-foreground truncate">
                    {book.authors?.join(', ')}
                  </p>
                </div>
                
                {/* Rating - support half stars */}
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {Array.from({ length: 5 }).map((_, i) => {
                    const starValue = i + 1;
                    const isFull = starValue <= Math.floor(book.rating_star);
                    const isHalf = !isFull && starValue - 0.5 <= book.rating_star && starValue > book.rating_star;
                    
                    if (isFull) {
                      return <Star key={i} className="w-4 h-4 fill-star text-star" />;
                    }
                    if (isHalf) {
                      return (
                        <div key={i} className="relative w-4 h-4">
                          <Star className="absolute w-4 h-4 text-star/30" />
                          <div className="absolute overflow-hidden w-[50%] h-4">
                            <Star className="w-4 h-4 fill-star text-star" />
                          </div>
                        </div>
                      );
                    }
                    return <Star key={i} className="w-4 h-4 text-star/30" />;
                  })}
                </div>
              </button>
            );
          })}
        </div>
        
        {/* Note about rankings */}
        <p className="text-xs text-muted-foreground text-center mt-4 pt-4 border-t">
          Based on your star ratings. Only books rated 4+ stars are shown.
        </p>
      </CardContent>
    </Card>
  );
}
